import styled, { css } from 'styled-components';
import { ButtonBox } from '@/components/UI/Button/Button.styles';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import {
  ACTIONS_HEIGHT,
  POPUP_SIZES,
} from '@/components/UI/Popup/Popup.constants';
import { PopupSize, PopupVariant } from '@/components/UI/Popup/Popup.types';
import { getFadeAnimation } from '@/styles/helpers';
import { Overlay } from '@/components/UI/Overlay';

const getPopupBoxStyles = (variant?: PopupVariant) => {
  switch (variant) {
    case '1':
      return css`
        border-radius: 14px;
      `;
    case '2':
      return css`
        border-radius: 22px;
      `;
    default:
      return css`
        border-radius: 7px;
      `;
  }
};

export const PopupBox = styled.div<{
  $isOpen: boolean;
  $size: PopupSize;
  $customSize?: { width: number | string; height?: number | string };
  $variant?: PopupVariant;
  $transparentBackground?: boolean;
}>`
  max-height: 95dvh;
  max-width: 92dvw;
  height: max-content;
  overflow: hidden;
  position: fixed;
  display: flex;
  flex-direction: column;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $size }) => POPUP_SIZES[$size]};
  z-index: 10200;
  background-color: ${({ theme, $transparentBackground }) =>
    $transparentBackground ? 'transparent' : theme.palette.primary.background};

  ${({ $isOpen }) => getFadeAnimation($isOpen)}

  ${({ $variant }) => getPopupBoxStyles($variant)}

  .popup__icon {
    margin: 20px auto -10px;
  }

  ${({ $customSize }) =>
    $customSize &&
    css`
      width: ${typeof $customSize.width === 'number'
        ? `${$customSize.width}px`
        : $customSize.width};
      height: ${(typeof $customSize.height === 'number'
        ? `${$customSize.height}px`
        : $customSize.height) || 'auto'};
    `}
`;

export const PopupTitle = styled(Typography.Header).attrs(() => ({
  $variant: '3',
}))<{
  $capitalizeTitle?: boolean;
}>`
  text-align: center;
  padding: 0 20px;
  margin-top: 20px;
  margin-bottom: 10px;

  ${({ $capitalizeTitle }) =>
    $capitalizeTitle &&
    css`
      text-transform: capitalize;
    `}
`;

export const PopupDescription = styled(Typography.Title)`
  text-align: center;
  color: ${({ theme }) => theme.palette.secondary.labels};
  padding: 0 20px 20px;
  margin-top: 10px;
`;

export const PopupDescriptionBox = styled.div`
  display: grid;
  gap: 15px;
  overflow-y: auto;
  padding: 0 20px 20px;
  margin-top: 10px;
`;

export const PopupActions = styled.div`
  display: flex;
  border-top: 1px solid ${({ theme }) => theme.palette.primary.border};
  height: ${ACTIONS_HEIGHT}px;
`;

export const PopupAction = styled(ButtonBox)<{
  $isRed?: boolean;
  $isGray?: boolean;
}>`
  width: 100%;
  border-radius: 0;
  color: ${({ theme, $isRed, $isGray }) =>
    $isRed
      ? theme.palette.system.red
      : $isGray
      ? theme.palette.secondary.labels
      : theme.palette.system.blue};
  font-size: 15px;

  &:nth-child(2) {
    border-left: 1px solid ${({ theme }) => theme.palette.primary.border};
  }
`;

export const PopupOverlay = styled(Overlay)`
  z-index: 10200;
`;
